/**
 * SeoMetaDiff Component
 * 
 * Field-level Diff für SEO Meta Daten.
 */

import { useTranslation } from 'react-i18next'
import type { SeoMetaContent } from '@/types/content-blocks'
import { TextDiff } from './TextDiff'

interface SeoMetaDiffProps {
  oldMeta: SeoMetaContent
  newMeta: SeoMetaContent
  oldLabel?: string
  newLabel?: string
}

interface FieldDiff {
  field: string
  label: string
  oldValue: string
  newValue: string
  hasChanges: boolean
}

export function SeoMetaDiff({
  oldMeta,
  newMeta,
  oldLabel = 'Alt',
  newLabel = 'Neu',
}: SeoMetaDiffProps) {
  const { t } = useTranslation()
  // Calculate field-level diffs
  const fields: FieldDiff[] = [
    {
      field: 'title',
      label: 'SEO Titel',
      oldValue: oldMeta.title || '',
      newValue: newMeta.title || '',
      hasChanges: (oldMeta.title || '') !== (newMeta.title || ''),
    },
    {
      field: 'description',
      label: 'Meta Beschreibung',
      oldValue: oldMeta.description || '',
      newValue: newMeta.description || '',
      hasChanges: (oldMeta.description || '') !== (newMeta.description || ''),
    },
  ]

  // Add keywords if present
  const oldKeywords = oldMeta.keywords?.join(', ') || ''
  const newKeywords = newMeta.keywords?.join(', ') || ''
  if (oldKeywords || newKeywords) {
    fields.push({
      field: 'keywords',
      label: 'Keywords',
      oldValue: oldKeywords,
      newValue: newKeywords,
      hasChanges: oldKeywords !== newKeywords,
    })
  }

  const hasAnyChanges = fields.some(f => f.hasChanges)

  if (!hasAnyChanges) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2" />
        {t('content.seoDiff')}
      </div>
    )
  }

  return (
    <div className="seo-meta-diff-container">
      {fields.map(field => (
        <div key={field.field} className="mb-4">
          <h6 className="mb-2">
            {field.label}
            {field.hasChanges && (
              <span className="badge bg-warning text-dark ms-2 small">
                {t('content.seoChanged')}
              </span>
            )}
          </h6>

          {field.hasChanges ? (
            <SeoFieldDiff
              oldValue={field.oldValue}
              newValue={field.newValue}
              oldLabel={oldLabel}
              newLabel={newLabel}
            />
          ) : (
            <div className="text-muted border rounded p-2 bg-dark bg-opacity-25">
              {field.newValue || <em>Nicht gesetzt</em>}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * Single field diff (simplified view)
 */
interface SeoFieldDiffProps {
  oldValue: string
  newValue: string
  oldLabel: string
  newLabel: string
}

function SeoFieldDiff({
  oldValue,
  newValue,
  oldLabel,
  newLabel,
}: SeoFieldDiffProps) {
  // For short fields, use simple side-by-side
  if (oldValue.length < 100 && newValue.length < 100) {
    return (
      <div className="row">
        <div className="col-6">
          <div className="border rounded p-2 bg-danger bg-opacity-10">
            <div className="small text-muted mb-1">{oldLabel}</div>
            <div className={oldValue ? 'text-decoration-line-through' : 'text-muted fst-italic'}>
              {oldValue || 'Nicht gesetzt'}
            </div>
          </div>
        </div>
        <div className="col-6">
          <div className="border rounded p-2 bg-success bg-opacity-10">
            <div className="small text-muted mb-1">{newLabel}</div>
            <div className={newValue ? 'fw-medium' : 'text-muted fst-italic'}>
              {newValue || 'Nicht gesetzt'}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // For longer fields, use text diff
  return (
    <TextDiff
      oldValue={oldValue}
      newValue={newValue}
      oldLabel={oldLabel}
      newLabel={newLabel}
      viewMode="unified"
    />
  )
}


